/* eslint-disable max-statements */
import React from 'react';
import compose from 'lodash/fp/compose';
import { useQuery, useApolloClient } from '@apollo/client';
import { withDevice } from '@catalogo/core-connect/device';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import { withAddToCart } from '@catalogo/service-americanas-cart/src/hocs/add-to-cart';
import { IWithAddToCartInjectedProps } from '@catalogo/service-americanas-cart/src/types/add-to-cart';
import { withModalWarrantyExperiment } from '@catalogo/service-americanas-product/src/experiments/modal-warranty-experiment';
import { IInjectedProps as IModalWarrantyExperimentProps } from '@catalogo/service-americanas-product/src/experiments/modal-warranty-experiment/types';
import { useAddToCartPDP } from '@catalogo/service-americanas-cart/src/hooks/add-to-cart-pdp';
import {
  selectButtonFlow,
  getUrl,
  getBuyButtonText,
} from '@catalogo/service-americanas-product/src/helpers/buy-button';
import { getSelectedStore } from '@catalogo/service-americanas-product/src/queries/store.graphql';
import { ToggleBuyTogether } from '@catalogo/service-americanas-product/src/payment/buy-together-vip.graphql';
import { withMetrics } from '@catalogo/core-metrics';
import { createMainOffer } from '@catalogo/core-metrics/src/helpers';
import { getPayments } from '@catalogo/service-americanas-product/src/payment/payments';
import { withCookies } from '@catalogo/core-connect/cookies';
import { withConfig } from '@catalogo/core-connect/config';
import { WithBuyButtonProps } from '../types';
import { sendMetrics, getBrand, getSellerType, addOfferFromComboVip } from '../helpers/buy-button';

// eslint-disable-next-line max-lines-per-function
export const withBuyButton = (Component: React.FC) => (
  props: WithBuyButtonProps & IWithAddToCartInjectedProps & IModalWarrantyExperimentProps
) => {
  const {
    isO2O,
    product,
    categoryUrl,
    currentSku,
    productId,
    isPreSale,
    fullWidth = true,
    productQty,
    experiment,
    device,
    sellerStoreId,
    dispatchMetrics,
    routerProps,
    discountProgressiveExperiment,
    setCookie,
    servicePDPinfoForWarranty,
    history,
    config,
    cartId,
    cartLines,
    addToCart,
    addToCartLoading,
    addToCartError,
    isAddedToCart,
    isUpdatedToCart,
    modalWarrantyExperiment,
  } = props;
  const { data } = useQuery(getSelectedStore);
  const { data: buyTogether } = useQuery(ToggleBuyTogether);

  let currentSellerStoreId = sellerStoreId;

  if (!currentSellerStoreId) {
    currentSellerStoreId = isO2O ? data?.selectedStore?.sellerStoreId : null;
  }

  const offer = currentSku?.offers?.result?.[0];
  const hasServices = (offer?.productServices ?? [])?.length > 0;
  const hasServiceSelected = !!servicePDPinfoForWarranty;
  const buyTogetherVIP = buyTogether?.buyTogetherVIP;

  const warrantyUrl = getUrl(
    productId,
    currentSku,
    currentSellerStoreId,
    buyTogether,
    productQty,
    servicePDPinfoForWarranty
  );

  const isMobile = device?.toLowerCase() === 'mobile';
  const brand = config?.brandNameLower;
  const url = selectButtonFlow({
    isO2O,
    sellerStoreId: currentSellerStoreId,
    currentSku,
    warrantyUrl,
    experiment,
    modalWarrantyExperiment,
    hasServices,
    hasServiceSelected,
    isMobile,
    brand,
  });

  const showModal = !url?.startsWith('/');

  const isInternational = currentSku?.offers?.result?.[0]?.currency === 'USD';

  const client = useApolloClient();
  const mainOffer = createMainOffer({ routerProps: routerProps, client: client, body: {} });
  const { mainPayment } = getPayments(mainOffer);
  const seller = offer?.seller;
  const productServices = (offer?.productServices as Array<{ services: Array<unknown> }>)?.[0]?.services;

  const currentProduct = {
    id: productId,
    name: product?.name,
    price: mainPayment?.price,
    quantity: productQty,
    category: categoryUrl,
    sku: currentSku?.id,
    sellerId: seller?.id,
    sellerName: seller?.name,
    hasProggresiveDiscount: discountProgressiveExperiment?.toLowerCase() === 'new',
    brand: getBrand(product),
    sellerType: getSellerType(seller, props.config),
    salesPrice: currentSku?.offers?.result?.[0]?.salesPrice,
    offer: { offerId: offer?.id, quantity: productQty, productSku: currentSku?.id },
    productServices,
    addServicesInDimension116: !isMobile && brand?.toLowerCase() === 'americanas' && productServices?.length > 0,
  };

  const offers = addOfferFromComboVip([currentProduct?.offer], buyTogetherVIP);

  const { handleAddProduct } = useAddToCartPDP({
    showModal,
    history,
    url,
    brand,
    addToCart,
    cartId,
    cartLines,
    isAddedToCart,
    isUpdatedToCart,
    addToCartLoading,
    addToCartError,
    offers,
    hasServices,
    servicePDPinfoForWarranty,
    currentSellerStoreId,
    buyTogetherVIP,
    currentSku,
    isMobile,
    modalWarrantyExperiment,
  });

  const withBuyButtonProps = {
    isInternational,
    url,
    fullWidth,
    buyButtonText: getBuyButtonText(isPreSale),
    showPreSaleButton: isPreSale && isMobile,
    sendMetrics: sendMetrics({
      dispatchMetrics,
      servicePDPinfoForWarranty,
      setCookie,
      products: [currentProduct],
      handleAddProduct,
    }),
    handleAddProduct,
  };

  return <Component {...props} {...withBuyButtonProps} />;
};

export default compose(
  withDevice,
  withProductQuantity,
  withMetrics,
  withCookies,
  withConfig,
  withAddToCart,
  withModalWarrantyExperiment,
  withBuyButton
);
